import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { Layout, DisplayState } from '@/types'
import { IDB_NAME, IDB_VERSION, IDB_STORES, STORAGE_KEYS } from '@/constants/storage-keys'
import { AUTO_SAVE_INTERVAL, EPHEMERAL_MODE } from '@/constants/defaults'
import { serialize } from '@/utils/serialize'
import { normalizeContentTransform } from '@/types/zone'
import { normalizeContentConfig } from '@/types/content'

export const usePersistenceStore = defineStore('persistence', () => {
  interface StateSnapshotPayload {
    layouts: Layout[]
    activeLayoutId: string | null
    displayStates: DisplayState[]
  }

  interface PersistedStateSnapshot {
    key: 'snapshot'
    stateRevision: number
    savedAtMs: number
    payload: StateSnapshotPayload
  }

  // State
  const isInitialized = ref(false)
  const isHydrated = ref(false)
  const isSaving = ref(false)
  const lastSaveTime = ref<number | null>(null)
  const lastError = ref<string | null>(null)

  // IndexedDB instance
  let db: IDBDatabase | null = null
  let initPromise: Promise<void> | null = null
  let autoSaveInterval: ReturnType<typeof setInterval> | null = null
  let writeChain: Promise<void> = Promise.resolve()

  const isEphemeralMode = EPHEMERAL_MODE
  const DEBUG_PERSISTENCE = import.meta.env.DEV && localStorage.getItem('msd_debug_state') === '1'

  function debugLog(...args: unknown[]) {
    if (!DEBUG_PERSISTENCE) return
    console.log('[persistence]', ...args)
  }

  function normalizeRevision(value: unknown): number {
    if (typeof value !== 'number' || !Number.isFinite(value)) return 0
    return Math.max(0, Math.floor(value))
  }

  function isNewerSnapshot(a: PersistedStateSnapshot | null, b: PersistedStateSnapshot | null): PersistedStateSnapshot | null {
    if (!a) return b
    if (!b) return a
    if (a.stateRevision !== b.stateRevision) {
      return a.stateRevision > b.stateRevision ? a : b
    }
    return a.savedAtMs >= b.savedAtMs ? a : b
  }

  async function enqueueWrite(operation: () => Promise<void>): Promise<void> {
    writeChain = writeChain.then(operation, operation)
    return writeChain
  }

  function setHydrated(value: boolean) {
    isHydrated.value = value
  }

  async function deleteIndexedDbDatabase(): Promise<void> {
    if (!('indexedDB' in window)) return

    if (db) {
      db.close()
      db = null
    }
    isInitialized.value = false
    initPromise = null

    await new Promise<void>((resolve, reject) => {
      const request = indexedDB.deleteDatabase(IDB_NAME)
      request.onsuccess = () => resolve()
      request.onblocked = () => resolve()
      request.onerror = () => reject(request.error ?? new Error('Failed to delete IndexedDB database'))
    })
  }

  async function wipeAllStorage(): Promise<void> {
    await enqueueWrite(async () => {
      isSaving.value = true
      try {
        stopAutoSave()
        await deleteIndexedDbDatabase()

        Object.values(STORAGE_KEYS).forEach((key) => {
          localStorage.removeItem(key)
        })

        const prefixedKeys: string[] = []
        for (let i = 0; i < localStorage.length; i += 1) {
          const key = localStorage.key(i)
          if (key && key.startsWith('setting_')) {
            prefixedKeys.push(key)
          }
        }
        prefixedKeys.forEach((key) => localStorage.removeItem(key))

        localStorage.removeItem('saved-layouts')

        isHydrated.value = false
        lastSaveTime.value = null
        lastError.value = null
        debugLog('wipe all storage completed')
      } finally {
        isSaving.value = false
      }
    })
  }

  function normalizeLayout(layout: Layout): Layout {
    return {
      ...layout,
      zones: layout.zones.map((zone) => ({
        ...zone,
        // Migrates legacy presentation payloads into normalized slides content on load/import.
        content: normalizeContentConfig(zone.content),
        contentTransform: normalizeContentTransform(zone.contentTransform),
      })),
      backgroundColor: layout.backgroundColor || '#000000',
      backgroundImage: layout.backgroundImage || '',
    }
  }

  function normalizeLayouts(layouts: Layout[]): Layout[] {
    return layouts.map(normalizeLayout)
  }

  function parseSnapshot(raw: unknown): PersistedStateSnapshot | null {
    if (!raw || typeof raw !== 'object') return null
    const record = raw as Partial<PersistedStateSnapshot>
    if (record.key !== 'snapshot') return null
    if (!record.payload || typeof record.payload !== 'object') return null
    const payload = record.payload as Partial<StateSnapshotPayload>
    if (!Array.isArray(payload.layouts)) return null

    return {
      key: 'snapshot',
      stateRevision: normalizeRevision(record.stateRevision),
      savedAtMs: typeof record.savedAtMs === 'number' && Number.isFinite(record.savedAtMs) ? record.savedAtMs : 0,
      payload: {
        layouts: normalizeLayouts(payload.layouts as Layout[]),
        activeLayoutId: typeof payload.activeLayoutId === 'string' ? payload.activeLayoutId : null,
        displayStates: Array.isArray(payload.displayStates) ? payload.displayStates as DisplayState[] : [],
      },
    }
  }

  function getSnapshotFromLocalStorage(): PersistedStateSnapshot | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.STATE_SNAPSHOT)
      if (!raw) return null
      return parseSnapshot(JSON.parse(raw))
    } catch {
      return null
    }
  }

  async function getSnapshotFromIndexedDb(): Promise<PersistedStateSnapshot | null> {
    try {
      const snapshot = await idbGet<PersistedStateSnapshot>(IDB_STORES.STATE, 'snapshot')
      return parseSnapshot(snapshot)
    } catch {
      return null
    }
  }

  function saveSnapshotToLocalStorage(record: PersistedStateSnapshot) {
    try {
      localStorage.setItem(STORAGE_KEYS.STATE_SNAPSHOT, JSON.stringify(record))
      localStorage.setItem(STORAGE_KEYS.LAYOUTS, JSON.stringify(serialize(record.payload.layouts)))
      localStorage.setItem(
        STORAGE_KEYS.DISPLAY_STATE,
        JSON.stringify({
          activeLayoutId: record.payload.activeLayoutId,
          displayStates: record.payload.displayStates,
          stateRevision: record.stateRevision,
        })
      )
    } catch (error) {
      console.warn('Failed to save snapshot to localStorage:', error)
    }
  }

  async function saveStateSnapshot(snapshot: {
    stateRevision: number
    activeLayoutId: string | null
    layouts: Layout[]
    displayStates?: DisplayState[]
  }): Promise<void> {
    if (isEphemeralMode) {
      debugLog('skip saveStateSnapshot (ephemeral mode)')
      return
    }

    const record: PersistedStateSnapshot = {
      key: 'snapshot',
      stateRevision: normalizeRevision(snapshot.stateRevision),
      savedAtMs: Date.now(),
      payload: {
        layouts: normalizeLayouts(snapshot.layouts),
        activeLayoutId: snapshot.activeLayoutId,
        displayStates: snapshot.displayStates || [],
      },
    }

    await enqueueWrite(async () => {
      isSaving.value = true
      try {
        await idbPut(IDB_STORES.STATE, serialize(record))
        saveSnapshotToLocalStorage(record)
        lastSaveTime.value = record.savedAtMs
        debugLog('save revision', record.stateRevision, 'savedAt', record.savedAtMs)
      } finally {
        isSaving.value = false
      }
    })
  }

  // Initialize IndexedDB
  async function initialize(): Promise<void> {
    if (isEphemeralMode) {
      isInitialized.value = true
      return
    }

    if (initPromise) return initPromise

    initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(IDB_NAME, IDB_VERSION)

      request.onerror = () => {
        const error = request.error?.message || 'Failed to open IndexedDB'
        lastError.value = error
        console.error('IndexedDB error:', error)
        reject(new Error(error))
      }

      request.onsuccess = () => {
        db = request.result
        isInitialized.value = true
        resolve()
      }

      request.onupgradeneeded = (event) => {
        const database = (event.target as IDBOpenDBRequest).result

        // Create object stores
        if (!database.objectStoreNames.contains(IDB_STORES.LAYOUTS)) {
          const layoutStore = database.createObjectStore(IDB_STORES.LAYOUTS, { keyPath: 'id' })
          layoutStore.createIndex('name', 'name', { unique: false })
          layoutStore.createIndex('updatedAt', 'updatedAt', { unique: false })
        }

        if (!database.objectStoreNames.contains(IDB_STORES.CONTENT)) {
          const contentStore = database.createObjectStore(IDB_STORES.CONTENT, { keyPath: 'id' })
          contentStore.createIndex('type', 'type', { unique: false })
        }

        if (!database.objectStoreNames.contains(IDB_STORES.SETTINGS)) {
          database.createObjectStore(IDB_STORES.SETTINGS, { keyPath: 'key' })
        }

        if (!database.objectStoreNames.contains(IDB_STORES.STATE)) {
          database.createObjectStore(IDB_STORES.STATE, { keyPath: 'key' })
        }
      }
    })

    return initPromise
  }

  // Generic IndexedDB operations
  async function idbGet<T>(storeName: string, key: string): Promise<T | undefined> {
    await initialize()
    if (!db) throw new Error('Database not initialized')

    return new Promise((resolve, reject) => {
      const transaction = db!.transaction(storeName, 'readonly')
      const store = transaction.objectStore(storeName)
      const request = store.get(key)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result as T | undefined)
    })
  }

  async function idbPut<T>(storeName: string, data: T): Promise<void> {
    await initialize()
    if (!db) throw new Error('Database not initialized')

    // Serialize data to convert Vue reactive proxies to plain objects
    const serializedData = serialize(data)

    return new Promise((resolve, reject) => {
      const transaction = db!.transaction(storeName, 'readwrite')
      const store = transaction.objectStore(storeName)
      const request = store.put(serializedData)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve()
    })
  }

  async function idbGetAll<T>(storeName: string): Promise<T[]> {
    await initialize()
    if (!db) throw new Error('Database not initialized')

    return new Promise((resolve, reject) => {
      const transaction = db!.transaction(storeName, 'readonly')
      const store = transaction.objectStore(storeName)
      const request = store.getAll()

      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result as T[])
    })
  }

  async function idbDelete(storeName: string, key: string): Promise<void> {
    await initialize()
    if (!db) throw new Error('Database not initialized')

    return new Promise((resolve, reject) => {
      const transaction = db!.transaction(storeName, 'readwrite')
      const store = transaction.objectStore(storeName)
      const request = store.delete(key)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve()
    })
  }

  // Layout operations
  async function saveLayout(layout: Layout): Promise<void> {
    if (isEphemeralMode) return
    await enqueueWrite(async () => {
      isSaving.value = true
      try {
        const normalized = normalizeLayout(layout)
        await idbPut(IDB_STORES.LAYOUTS, normalized)
        lastSaveTime.value = Date.now()
      } catch (error) {
        lastError.value = (error as Error).message
        throw error
      } finally {
        isSaving.value = false
      }
    })
  }

  async function getLayout(id: string): Promise<Layout | undefined> {
    if (isEphemeralMode) return undefined
    try {
      const layout = await idbGet<Layout>(IDB_STORES.LAYOUTS, id)
      return layout ? normalizeLayout(layout) : undefined
    } catch (error) {
      lastError.value = (error as Error).message
      return undefined
    }
  }

  async function getAllLayouts(): Promise<Layout[]> {
    if (isEphemeralMode) return []
    try {
      const layouts = await idbGetAll<Layout>(IDB_STORES.LAYOUTS)
      return normalizeLayouts(layouts)
    } catch (error) {
      lastError.value = (error as Error).message
      return []
    }
  }

  async function deleteLayout(id: string): Promise<void> {
    if (isEphemeralMode) return
    await enqueueWrite(async () => {
      try {
        await idbDelete(IDB_STORES.LAYOUTS, id)
      } catch (error) {
        lastError.value = (error as Error).message
        throw error
      }
    })
  }

  function getLayoutsFromLocalStorage(): Layout[] {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.LAYOUTS)
      const parsed = data ? JSON.parse(data) as Layout[] : []
      return normalizeLayouts(parsed)
    } catch {
      return []
    }
  }

  // Settings operations
  async function saveSetting(key: string, value: unknown): Promise<void> {
    if (isEphemeralMode) return
    await enqueueWrite(async () => {
      try {
        const serializedValue = serialize(value)
        await idbPut(IDB_STORES.SETTINGS, { key, value: serializedValue })
        localStorage.setItem(`setting_${key}`, JSON.stringify(serializedValue))
      } catch (error) {
        lastError.value = (error as Error).message
      }
    })
  }

  async function getSetting<T>(key: string): Promise<T | undefined> {
    if (isEphemeralMode) return undefined
    try {
      const result = await idbGet<{ key: string; value: T }>(IDB_STORES.SETTINGS, key)
      return result?.value
    } catch {
      // Fallback to localStorage
      try {
        const data = localStorage.getItem(`setting_${key}`)
        return data ? JSON.parse(data) : undefined
      } catch {
        return undefined
      }
    }
  }

  // State operations (for recovery)
  async function saveAppState(state: {
    activeLayoutId: string | null
    displayStates: DisplayState[]
    stateRevision?: number
    layouts?: Layout[]
  }): Promise<void> {
    if (isEphemeralMode) {
      debugLog('skip saveAppState (ephemeral mode)')
      return
    }

    // Prefer new snapshot path when layouts/revision are available.
    if (state.layouts && typeof state.stateRevision === 'number') {
      await saveStateSnapshot({
        stateRevision: state.stateRevision,
        activeLayoutId: state.activeLayoutId,
        layouts: state.layouts,
        displayStates: state.displayStates,
      })
      return
    }

    await enqueueWrite(async () => {
      try {
        // Serialize to convert Vue reactive proxies to plain objects
        const serializedState = serialize(state)
        await idbPut(IDB_STORES.STATE, { key: 'app', state: serializedState, timestamp: Date.now() })
        localStorage.setItem(STORAGE_KEYS.DISPLAY_STATE, JSON.stringify(serializedState))
      } catch (error) {
        console.warn('Failed to save app state:', error)
      }
    })
  }

  async function getAppState(): Promise<{
    state: { activeLayoutId: string | null; displayStates: DisplayState[] }
    timestamp: number
  } | null> {
    if (isEphemeralMode) return null
    try {
      const result = await idbGet<{
        key: string
        state: { activeLayoutId: string | null; displayStates: DisplayState[] }
        timestamp: number
      }>(IDB_STORES.STATE, 'app')

      if (result) {
        return { state: result.state, timestamp: result.timestamp }
      }
      return null
    } catch {
      // Fallback to localStorage
      try {
        const data = localStorage.getItem(STORAGE_KEYS.DISPLAY_STATE)
        if (data) {
          return { state: JSON.parse(data), timestamp: Date.now() }
        }
      } catch {
        // Ignore
      }
      return null
    }
  }

  // Recovery
  async function recoverState(): Promise<{
    layouts: Layout[]
    activeLayoutId: string | null
    displayStates: DisplayState[]
    stateRevision: number
  }> {
    if (isEphemeralMode) {
      debugLog('skip recoverState (ephemeral mode)')
      isHydrated.value = false
      return {
        layouts: [],
        activeLayoutId: null,
        displayStates: [],
        stateRevision: 0,
      }
    }

    isHydrated.value = false
    debugLog('hydrate start')
    const idbSnapshot = await getSnapshotFromIndexedDb()
    const localSnapshot = getSnapshotFromLocalStorage()
    const selectedSnapshot = isNewerSnapshot(idbSnapshot, localSnapshot)

    if (selectedSnapshot) {
      debugLog('hydrate selected snapshot', {
        revision: selectedSnapshot.stateRevision,
        savedAtMs: selectedSnapshot.savedAtMs,
      })
      isHydrated.value = true
      debugLog('hydrate end')
      return {
        layouts: selectedSnapshot.payload.layouts,
        activeLayoutId: selectedSnapshot.payload.activeLayoutId,
        displayStates: selectedSnapshot.payload.displayStates,
        stateRevision: selectedSnapshot.stateRevision,
      }
    }

    // Backward-compatibility fallback for older persisted formats.
    let layouts = await getAllLayouts()
    const appState = await getAppState()
    let revision = normalizeRevision((appState?.state as { stateRevision?: unknown } | undefined)?.stateRevision)

    // Fallback to localStorage if IndexedDB is empty
    if (layouts.length === 0) {
      const cachedLayouts = getLayoutsFromLocalStorage()
      if (cachedLayouts.length > 0) {
        layouts = cachedLayouts
      }
    }

    if (revision === 0 && layouts.length > 0) {
      revision = 1
    }

    isHydrated.value = true
    debugLog('hydrate end (legacy fallback)', { revision, layouts: layouts.length })
    return {
      layouts,
      activeLayoutId: appState?.state.activeLayoutId || null,
      displayStates: appState?.state.displayStates || [],
      stateRevision: revision,
    }
  }

  // Auto-save
  function startAutoSave(getState: () => {
    layouts: Layout[]
    activeLayoutId: string | null
    stateRevision?: number
    displayStates?: DisplayState[]
  }) {
    if (isEphemeralMode) {
      debugLog('skip auto-save start (ephemeral mode)')
      return
    }

    if (autoSaveInterval) return
    if (!isHydrated.value) {
      debugLog('skip auto-save start before hydration')
      return
    }

    autoSaveInterval = setInterval(async () => {
      const state = getState()
      await saveStateSnapshot({
        stateRevision: normalizeRevision(state.stateRevision),
        activeLayoutId: state.activeLayoutId,
        layouts: state.layouts,
        displayStates: state.displayStates || [],
      })
    }, AUTO_SAVE_INTERVAL)
  }

  function stopAutoSave() {
    if (autoSaveInterval) {
      clearInterval(autoSaveInterval)
      autoSaveInterval = null
    }
  }

  // Export/Import
  async function exportData(): Promise<string> {
    const recovered = await recoverState()
    const layouts = recovered.layouts
    const settings: Record<string, unknown> = {}

    // Get all settings
    try {
      const allSettings = await idbGetAll<{ key: string; value: unknown }>(IDB_STORES.SETTINGS)
      allSettings.forEach((s) => {
        settings[s.key] = s.value
      })
    } catch {
      // Ignore
    }

    return JSON.stringify(
      {
        version: 1,
        exportedAt: Date.now(),
        stateRevision: recovered.stateRevision,
        layouts,
        settings,
      },
      null,
      2
    )
  }

  async function importData(jsonString: string): Promise<void> {
    if (isEphemeralMode) {
      debugLog('skip importData (ephemeral mode)')
      return
    }

    let data: unknown
    try {
      data = JSON.parse(jsonString)
    } catch {
      throw new Error('Invalid JSON: failed to parse import data')
    }

    if (!data || typeof data !== 'object') {
      throw new Error('Invalid import data: expected an object')
    }

    const importObj = data as Record<string, unknown>

    if (importObj.layouts) {
      if (!Array.isArray(importObj.layouts)) {
        throw new Error('Invalid import data: layouts must be an array')
      }

      for (const layout of importObj.layouts) {
        if (!isValidLayout(layout)) {
          console.warn('Skipping invalid layout during import:', layout)
          continue
        }
        await saveLayout(layout as Layout)
      }
    }

    if (importObj.settings) {
      if (typeof importObj.settings !== 'object' || importObj.settings === null) {
        throw new Error('Invalid import data: settings must be an object')
      }

      for (const [key, value] of Object.entries(importObj.settings as Record<string, unknown>)) {
        if (typeof key !== 'string' || !key) continue
        await saveSetting(key, value)
      }
    }

    // Persist imported state as a snapshot when possible.
    if (importObj.layouts && Array.isArray(importObj.layouts)) {
      await saveStateSnapshot({
        stateRevision: normalizeRevision(importObj.stateRevision),
        activeLayoutId: null,
        layouts: importObj.layouts as Layout[],
        displayStates: [],
      })
    }
  }

  // Validate that a layout has the required structure
  function isValidLayout(layout: unknown): boolean {
    if (!layout || typeof layout !== 'object') return false
    const l = layout as Record<string, unknown>

    if (typeof l.id !== 'string' || !l.id) return false
    if (typeof l.name !== 'string') return false
    if (!Array.isArray(l.zones)) return false

    // Validate each zone has at minimum an id and bounds
    for (const zone of l.zones) {
      if (!zone || typeof zone !== 'object') return false
      const z = zone as Record<string, unknown>
      if (typeof z.id !== 'string' || !z.id) return false
      if (!z.bounds || typeof z.bounds !== 'object') return false
    }

    return true
  }

  // Cleanup
  function destroy() {
    stopAutoSave()
    if (db) {
      db.close()
      db = null
    }
    isInitialized.value = false
    isHydrated.value = false
    initPromise = null
  }

  return {
    // State
    isInitialized,
    isHydrated,
    isEphemeralMode,
    isSaving,
    lastSaveTime,
    lastError,

    // Actions
    initialize,
    destroy,
    wipeAllStorage,
    setHydrated,

    // Layout operations
    saveLayout,
    getLayout,
    getAllLayouts,
    deleteLayout,

    // Settings
    saveSetting,
    getSetting,

    // State
    saveAppState,
    saveStateSnapshot,
    getAppState,
    recoverState,

    // Auto-save
    startAutoSave,
    stopAutoSave,

    // Export/Import
    exportData,
    importData,
  }
})
